import Phaser from "phaser";
import { ItemDefinition, Recipe, TILE_SIZE } from "../../../../models/common";
import { COLORS } from "../../../../ui/colors";
import { SpriteLoader } from "../../../../sprite-loader";
import { ItemSlot } from "../../../../ui/item-slot";
import { drawWidgetBackground, QUEST_WIDGET_W } from "./widget-background";

/**
 * RecipeWidget: Shows the recipe the player is closest to completing (fewest missing materials).
 * Usage: new RecipeWidget(scene, x, y, recipes, itemDefs, playerMaterialCounts)
 */
export class RecipeWidget extends Phaser.GameObjects.Container {
  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    recipes: Recipe[],
    itemDefs: ItemDefinition[],
    playerMaterialCounts: Record<number, number>
  ) {
    super(scene, x, y);

    // Find the recipe with the fewest missing materials
    let bestRecipe: Recipe | null = null;
    let minMissing = Infinity;
    for (const recipe of recipes) {
      let missing = 0;
      for (const mat of recipe.materials) {
        const owned = playerMaterialCounts[mat.itemId] || 0;
        if (owned < mat.quantity) missing += mat.quantity - owned;
      }
      if (missing < minMissing) {
        minMissing = missing;
        bestRecipe = recipe;
      }
    }
    if (!bestRecipe) return;

    // Get item definition for the crafted item
    const itemDef = itemDefs.find((def) => def.id === bestRecipe!.itemId);
    if (!itemDef) return;

    // Layout constants
    const LEFT_PADDING = 60;
    const ITEM_SIZE = TILE_SIZE * 1.5;
    const slotSpacing = 52;
    const slotSize = 40;
    const matStartX = ITEM_SIZE / 2 + slotSpacing / 2;

    // Label (created before bg so we have label.height for bg dimensions)
    const label = scene.add.text(32, -14, `Get materials for this`, {
      fontSize: "14px",
      color: COLORS.YELLOW,
      fontFamily: "Anton",
      fontStyle: "bold",
      align: "left",
    });
    label.setOrigin(0, 0);

    // Background
    const rowY = label.height + 8;
    const bgX = 8;
    const bgY = -(14 + 6);
    const bgW = QUEST_WIDGET_W;
    const bgH = Math.abs(bgY) + rowY + ITEM_SIZE / 2 + 16;
    const bg = scene.add.graphics();
    drawWidgetBackground(bg, bgX, bgY, bgW, bgH);
    this.add(bg);
    this.add(label);

    // Row container for item + materials
    const row = scene.add.container(LEFT_PADDING, rowY);

    // Item icon (twice as big)
    const itemSprite = SpriteLoader.createSprite(
      scene,
      itemDef.spriteSheet,
      itemDef.spriteX,
      itemDef.spriteY,
      0,
      0,
      true,
    );
    itemSprite.displayWidth = ITEM_SIZE;
    itemSprite.displayHeight = ITEM_SIZE;
    itemSprite.setOrigin(0.5);
    row.add(itemSprite);

    // Materials in slots, spaced to right of item icon
    bestRecipe.materials.forEach((mat, i) => {
      const matX = matStartX + i * slotSpacing;
      // Create slot
      const slot = new ItemSlot(matX - slotSize / 2, -slotSize / 2, true);
      const slotGraphic = slot.createRectangle(scene);
      row.add(slotGraphic);
      // Material icon centered in slot
      const matDef = itemDefs.find((def) => def.id === mat.itemId);
      if (!matDef) return;
      const matSprite = SpriteLoader.createSprite(
        scene,
        matDef.spriteSheet,
        matDef.spriteX,
        matDef.spriteY,
        matX,
        0,
        true,
      );
      matSprite.displayWidth = TILE_SIZE;
      matSprite.displayHeight = TILE_SIZE;
      matSprite.setOrigin(0.5);
      const owned = playerMaterialCounts[mat.itemId] || 0;
      if (owned < mat.quantity) matSprite.setTint(0xff6666);
      row.add(matSprite);
      // Quantity text
      const qtyText = scene.add.text(
        matX + slotSize / 2 - 5,
        -14,
        `x${mat.quantity}`,
        {
          fontSize: "12px",
          color: owned >= mat.quantity ? COLORS.WHITE : COLORS.ORANGE,
          fontFamily: "Anton",
          fontStyle: "bold",
        },
      );
      qtyText.setOrigin(1, 0.5);
      row.add(qtyText);
      // Owned text
      const ownedText = scene.add.text(
        matX + slotSize / 2 - 5,
        14,
        `(${owned})`,
        {
          fontSize: "12px",
          color: owned >= mat.quantity ? COLORS.GREEN : COLORS.ORANGE,
          fontFamily: "Anton",
        },
      );
      ownedText.setOrigin(1, 0.5);
      row.add(ownedText);
    });

    this.add(row);
  }
}
